import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { forgetEffectiveFlags } from '../admin/featureFlags/featureFlagsApi';
import { forgetRoleAccess } from '../admin/roleCenter/roleAccessApi';
import { usageRecorder } from '../usage/usageRecorder';
import { codeFromHash } from '../admin/users/impersonationRules';
import {
  IMPERSONATE_PATH,
  impersonationEnded,
  leaveEndedImpersonation,
  loginToken,
  startImpersonationTab,
} from './sessionToken';

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

export const EXCHANGE_ERROR =
  'Nie udało się zalogować jako ten użytkownik. Kod mógł wygasnąć albo został już użyty — zamknij tę kartę i spróbuj jeszcze raz z listy użytkowników.';
export const NO_ADMIN_SESSION =
  'Ta karta nie widzi Twojej sesji administratora. Zaloguj się w aplikacji i spróbuj jeszcze raz z listy użytkowników.';
export const ENDED_TEXT =
  'Sesja pracy jako inny użytkownik została zakończona. Możesz zamknąć tę kartę — Twoja karta administratora działa dalej.';

/**
 * Wymiana kodu „Zaloguj jako” na sesję tej karty (decyzja Bartka 02.11.2026).
 * Kod jest we fragmencie adresu; wymieniamy go z tokenem administratora
 * (`localStorage`), bo API wydaje sesję tylko temu, kto ją zaczął.
 */
export async function exchangeImpersonationCode(code: string): Promise<string> {
  const admin = loginToken();
  if (!admin) throw new Error(NO_ADMIN_SESSION);
  let res: Response;
  try {
    res = await fetch(`${API}/auth/impersonate/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${admin}` },
      body: JSON.stringify({ code }),
    });
  } catch {
    throw new Error(EXCHANGE_ERROR);
  }
  const body = (await res.json().catch(() => ({}))) as { access_token?: unknown; message?: unknown };
  if (!res.ok || typeof body.access_token !== 'string') {
    throw new Error(typeof body.message === 'string' && body.message ? body.message : EXCHANGE_ERROR);
  }
  return body.access_token;
}

/**
 * Wymiany w toku — po kodzie. Kod działa raz, a React w trybie deweloperskim
 * (`StrictMode`) uruchamia efekt dwa razy; druga wymiana tego samego kodu
 * dostałaby 410 i przykryła udaną pierwszą (e2e s43, 02.11.2026). Jedna
 * obietnica na kod — oba przebiegi czekają na tę samą odpowiedź.
 */
const exchanges = new Map<string, Promise<string>>();

/** Wymiana kodu raz na kartę — kolejne wywołania dostają tę samą obietnicę. */
export function exchangeOnce(code: string): Promise<string> {
  const pending = exchanges.get(code);
  if (pending) return pending;
  const started = exchangeImpersonationCode(code);
  exchanges.set(code, started);
  return started;
}

/**
 * Kod z adresu — czytany raz na wczytanie karty i od razu zdejmowany z adresu
 * (nie zostaje w historii ani po odświeżeniu). Poza stanem komponentu, bo
 * React w trybie deweloperskim wywołuje inicjator stanu dwa razy, a ekran
 * bywa montowany ponownie, gdy react-admin kończy wczytywanie tras — drugi
 * odczyt widziałby adres już bez kodu (e2e s43, 02.11.2026).
 */
let capturedCode: string | null = null;

export function takeCodeFromLocation(): string | null {
  const found = codeFromHash(window.location.hash);
  if (found) {
    capturedCode = found;
    window.history.replaceState(null, '', `/#${IMPERSONATE_PATH}`);
  }
  return capturedCode;
}

/** Tylko testy: kolejny „wczytany” ekran bez kodu z poprzedniego. */
export function forgetCapturedCode(): void {
  capturedCode = null;
  exchanges.clear();
}

/**
 * `/impersonate` — ekran karty przejęcia poza układem panelu:
 * - `#/impersonate?code=…` — wymiana kodu, zapis sesji tej karty, przejście na pulpit;
 * - `?ended=1` albo bez kodu — „sesja zakończona” z zamknięciem karty.
 */
export const ImpersonatePage = () => {
  const [code] = useState<string | null>(takeCodeFromLocation);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<'exchanging' | 'ended' | 'error'>(code ? 'exchanging' : 'ended');

  useEffect(() => {
    if (!code) return undefined;
    let active = true;
    exchangeOnce(code)
      .then((token) => {
        // Nic z sesji administratora nie idzie z tokenem pracownika: pomiar,
        // przełączniki i uprawnienia od nowa (02.11.2026). Zapis jest
        // jednakowy przy powtórzonym efekcie — karta i tak przechodzi dalej.
        usageRecorder.reset();
        forgetEffectiveFlags();
        forgetRoleAccess();
        startImpersonationTab(token);
        // Karta przejęcia nie trzyma odnośnika do karty administratora
        // (przegląd #582, D5) — otwarta skryptem miałaby go w `opener`.
        try {
          window.opener = null;
        } catch {
          // Przeglądarka nie pozwala — bez znaczenia dla sesji.
        }
        window.location.replace('/');
      })
      .catch((e: unknown) => {
        if (!active) return;
        setError(e instanceof Error ? e.message : EXCHANGE_ERROR);
        setState('error');
      });
    return () => {
      active = false;
    };
  }, [code]);

  return (
    <Box
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
      data-testid="impersonate-page"
    >
      <Stack spacing={2} sx={{ maxWidth: 560, alignItems: 'center', textAlign: 'center' }}>
        {state === 'exchanging' ? (
          <>
            <CircularProgress />
            <Typography>Logowanie jako wybrany użytkownik…</Typography>
          </>
        ) : null}
        {state === 'error' ? (
          <Alert severity="error" data-testid="impersonate-page-error">
            {error}
          </Alert>
        ) : null}
        {state === 'ended' ? (
          <Alert severity="info" data-testid="impersonate-ended">
            {ENDED_TEXT}
          </Alert>
        ) : null}
        {state !== 'exchanging' ? (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => window.close()} data-testid="impersonate-close-tab">
              Zamknij kartę
            </Button>
            {impersonationEnded() || state === 'error' ? (
              <Button
                onClick={() => {
                  // Świadomy powrót do własnego konta w tej karcie.
                  leaveEndedImpersonation();
                  window.location.replace('/');
                }}
                data-testid="impersonate-back-to-own"
              >
                Przejdź do swojego konta
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ImpersonatePage;
